import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Chrome } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export default function Login2() {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const canvasRef = useRef(null);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [orbitRotation, setOrbitRotation] = useState(0);

  // Slides pour le carousel
  const slides = [
    {
      title: "Correspondances RNCP intelligentes",
      description: "Analysez et comparez automatiquement les référentiels de compétences"
    },
    {
      title: "Visualisation interactive",
      description: "Explorez vos données avec des graphiques dynamiques et intuitifs"
    },
    {
      title: "Collaboration en temps réel",
      description: "Travaillez en équipe sur vos projets de certification"
    }
  ];

  // Animation des orbs flottants
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Orbs flottants
    const orbs = Array.from({ length: 8 }, () => ({
      x: Math.random() * canvas.width / dpr,
      y: Math.random() * canvas.height / dpr,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
      radius: 40 + Math.random() * 80,
      opacity: 0.1 + Math.random() * 0.15,
      hue: Math.random() * 60 + 180 // Bleus/verts
    }));

    let animationId;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);

      orbs.forEach(orb => {
        // Mise à jour position avec interpolation
        orb.x += orb.vx;
        orb.y += orb.vy;

        // Rebonds sur les bords
        if (orb.x < -orb.radius || orb.x > canvas.width / dpr + orb.radius) orb.vx *= -1;
        if (orb.y < -orb.radius || orb.y > canvas.height / dpr + orb.radius) orb.vy *= -1;

        // Dessin de l'orb avec gradient radial
        const gradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.radius);
        gradient.addColorStop(0, `hsla(${orb.hue}, 70%, 60%, ${orb.opacity})`);
        gradient.addColorStop(1, `hsla(${orb.hue}, 70%, 60%, 0)`);

        ctx.fillStyle = gradient;
        ctx.globalCompositeOperation = 'lighter';
        ctx.beginPath();
        ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  // Animation de rotation en orbite pour les icônes (1 tour en 5 secondes)
  useEffect(() => {
    let startTime = Date.now();
    let animationId;

    const rotateOrbit = () => {
      const elapsed = Date.now() - startTime;
      const rotation = (elapsed / 5000) * 360; // 360° en 5000ms (5 secondes)
      setOrbitRotation(rotation % 360);
      animationId = requestAnimationFrame(rotateOrbit);
    };

    rotateOrbit();
    return () => cancelAnimationFrame(animationId);
  }, []);

  // Redirection si déjà connecté
  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      await login({ email: formData.email, password: formData.password });
    } catch (error) {
      console.error('Erreur de connexion:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 112, 223, 0.2), rgba(60, 175, 141, 0.2)), url('https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&q=80')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Conteneur principal avec effet flottant */}
      <div className="w-full max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden">
        <div className="grid lg:grid-cols-5 min-h-[600px]">

          {/* COLONNE GAUCHE - Formulaire de connexion */}
          <div className="lg:col-span-2 p-8 lg:p-12 flex flex-col justify-center">

            {/* Logo / Marque */}
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center text-white font-bold">
                O
              </div>
              <span className="text-xl font-bold text-gray-900">Orbloom</span>
            </div>

            {/* Titre principal */}
            <h1 className="text-4xl font-bold text-gray-900 mb-3">
              Bienvenue !
            </h1>
            <p className="text-gray-600 mb-8">
              Connectez-vous pour accéder à votre espace de travail
            </p>

            {/* Boutons sociaux */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-sm">
                <Chrome className="w-5 h-5" />
                <span className="text-sm font-medium">Google</span>
              </button>
              <button className="flex items-center justify-center gap-2 px-4 py-3 border border-gray-300 rounded-full hover:bg-gray-50 transition-colors shadow-sm">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                </svg>
                <span className="text-sm font-medium">GitHub</span>
              </button>
            </div>

            {/* Séparateur */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">ou continuer avec email</span>
              </div>
            </div>

            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-4">

              {/* Email */}
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  placeholder="Adresse email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  required
                />
              </div>

              {/* Password */}
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mot de passe"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full pl-12 pr-12 py-3 bg-gray-50 border border-gray-200 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-gray-700">Se souvenir de moi</span>
                </label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-primary-600 hover:text-primary-700 font-medium"
                >
                  Mot de passe oublié ?
                </button>
              </div>

              {/* Bouton de connexion */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold rounded-full hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>

            {/* Lien inscription */}
            <p className="text-center text-sm text-gray-600 mt-6">
              Vous n'avez pas de compte ?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-primary-600 hover:text-primary-700 font-semibold"
              >
                Créer un compte
              </button>
            </p>
          </div>

          {/* COLONNE DROITE - Zone visuelle */}
          <div className="lg:col-span-3 relative bg-gradient-to-br from-primary-50 to-secondary-50 p-12 flex flex-col items-center justify-center overflow-hidden">

            {/* Canvas pour les orbs animés */}
            <canvas
              ref={canvasRef}
              className="absolute inset-0 w-full h-full"
              style={{ mixBlendMode: 'screen' }}
            />

            {/* Illustration centrale */}
            <div className="relative z-10 text-center max-w-md">

              {/* Cercles concentriques décoratifs */}
              <div className="relative w-80 h-80 mx-auto mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-primary-200/30 to-secondary-200/30 rounded-full blur-3xl"></div>
                <div className="absolute inset-8 bg-gradient-to-br from-primary-100/40 to-secondary-100/40 rounded-full blur-2xl"></div>
                <div className="absolute inset-16 bg-white/50 rounded-full backdrop-blur-sm flex items-center justify-center">

                  {/* Icône centrale stylisée */}
                  <div className="relative w-32 h-32">
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl rotate-12 opacity-20"></div>
                    <div className="absolute inset-2 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl flex items-center justify-center text-white">
                      <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </div>
                  </div>

                  {/* Icônes en orbite */}
                  {/* Icône 1 - 📊 */}
                  <div
                    className="absolute w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center will-change-transform"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate(-50%, -50%) rotate(${orbitRotation}deg) translateX(120px) rotate(-${orbitRotation}deg)`
                    }}
                  >
                    <span className="text-2xl">📊</span>
                  </div>

                  {/* Icône 2 - 📋 */}
                  <div
                    className="absolute w-16 h-16 bg-white rounded-xl shadow-lg flex items-center justify-center will-change-transform"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate(-50%, -50%) rotate(${orbitRotation + 120}deg) translateX(120px) rotate(-${orbitRotation + 120}deg)`
                    }}
                  >
                    <span className="text-3xl">📋</span>
                  </div>

                  {/* Icône 3 - ✓ */}
                  <div
                    className="absolute w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center will-change-transform"
                    style={{
                      left: '50%',
                      top: '50%',
                      transform: `translate(-50%, -50%) rotate(${orbitRotation + 240}deg) translateX(120px) rotate(-${orbitRotation + 240}deg)`
                    }}
                  >
                    <span className="text-xl">✓</span>
                  </div>
                </div>
              </div>

              {/* Texte du slide */}
              <h2 className="text-2xl font-bold text-gray-900 mb-3 transition-all duration-500">
                {slides[currentSlide].title}
              </h2>
              <p className="text-gray-600 mb-8 transition-all duration-500">
                {slides[currentSlide].description}
              </p>

              {/* Indicateurs carousel */}
              <div className="flex items-center justify-center gap-2">
                {slides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`h-2 rounded-full transition-all ${
                      index === currentSlide
                        ? 'w-8 bg-primary-500'
                        : 'w-2 bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
